import { getLocalTimeZone, today } from "@internationalized/date";
import { UseQueryResult } from "@tanstack/react-query";
import { DateTimeRangePicker } from "features/CenterContent/RoleContent/ProductionDeviation/components/DateTimeRangePicker";
import { ShowTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/components/ShowTrainSeries";
import { ProductionDeviationForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import {
  makeBatchProps,
  Path,
  useProductionDeviationPath,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FormInputs } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/batchSections/FormInputs";
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { DropsStaticAlert, Flex, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

type ProductionDeviationFormInputsProps = {
  path: Path;
  isEditMode: boolean;
  onClose: () => void;
  onBack: () => void;
  onSubmit: () => void;
  opInfoTypes: UseQueryResult<OperationalInformationTypes>;
  trainSeries: string[];
  trigger: () => Promise<boolean>;
};

export const ProductionDeviationFormInputs: FC<
  ProductionDeviationFormInputsProps
> = ({
  path,
  isEditMode,
  onClose,
  onBack,
  onSubmit,
  opInfoTypes,
  trainSeries,
  trigger,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, status } = opInfoTypes;
  const { control, setValue } = useFormContext<ProductionDeviationForm>();
  const batchTrainForm = useWatch({
    name: path("batchTrainForm"),
    control,
  });

  /**
   * Propagate changes to the other trainSeries
   */
  useEffect(() => {
    const otherTrainSeries = trainSeries.slice(1);
    otherTrainSeries.forEach((trainSerie) => {
      const { path: otherTrainSeriePath } =
        useProductionDeviationPath(trainSerie);
      const directionsIndexes = [0, 1];
      directionsIndexes.forEach((i) => {
        const field = otherTrainSeriePath(`batchTrainForm.${i}.trainForm`);
        setValue(field, batchTrainForm[i].trainForm);
      });
    });
  }, [batchTrainForm]);

  const mode = isEditMode ? "edit" : "create";

  return (
    <ActionModal
      title={`${isEditMode ? t("Endre") : t("Opprett")} ${t("produksjonsavvik")}`}
      actionTitle={`${t("Neste")} →`}
      onClose={onClose}
      onBack={isEditMode ? undefined : onBack}
      onSubmit={async () => {
        const success = await trigger();
        if (!success) return;
        onSubmit();
      }}
      loading={false}
      isSuccess={false}
      isError={false}
      successMessage=""
      failureMessage=""
      subHeaders={[<ShowTrainSeries trainSeries={trainSeries} />]}
    >
      <Flex flexDir="column" gap="6px" w="100%" mb="12px">
        {isEditMode && (
          <DropsStaticAlert variant="info" width="100%">
            {t("Opprett nytt avvik for å endre periode eller hendelsetype.")}
          </DropsStaticAlert>
        )}
        <Flex flexDir="column" gap="6px">
          <Text
            marginTop={isEditMode ? "9px" : undefined}
            color={isEditMode ? theme.colorTextDisabled : undefined}
          >
            {t("Tidsperiode")}
          </Text>
          <DateTimeRangePicker
            durationsField="durations"
            minValue={isEditMode ? undefined : today(getLocalTimeZone())}
            maxValue={
              isEditMode
                ? undefined
                : today(getLocalTimeZone()).add({ days: 13 })
            }
            isDisabled={isEditMode}
          />
        </Flex>
      </Flex>
      <Flex w="100%">
        {status === "pending" && (
          <SkeletonLoader skeletonType="trainInfoForm" />
        )}
        {status === "error" && <FailureMessage flex={1} m="0px" maxW="100%" />}
        {status === "success" && (
          <FormInputs {...makeBatchProps(path, data, mode)} />
        )}
      </Flex>
    </ActionModal>
  );
};
